// src/pages/AllianceManagement/VotingTab.js

import React, { useEffect, useState } from 'react';
import CreateVoteModal from '../../modals/CreateVoteModal';
import ResultsModal from '../../modals/ResultsModal';
import axiosInstance from '../../utils/axiosInstance';
import './css/VotingTab.css';
import { AppButton } from '../../components/ui';

const VotingTab = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isResultsModalOpen, setIsResultsModalOpen] = useState(false);
  const [selectedVote, setSelectedVote] = useState(null);
  const [votes, setVotes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [createdVoteLink, setCreatedVoteLink] = useState('');

  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setCreatedVoteLink(''); // Reset created vote link when closing the modal
  };

  const handleOpenResults = async (voteId) => {
    setIsLoading(true);
    try {
      const response = await axiosInstance.get(`/votes/${voteId}`);
      setSelectedVote(response.data);
      setIsResultsModalOpen(true);
    } catch (err) {
      console.error('Error fetching vote details:', err);
      alert('Failed to fetch vote details.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseResultsModal = () => {
    setSelectedVote(null);
    setIsResultsModalOpen(false);
  };

  const fetchVotes = async () => {
    setIsLoading(true);
    try {
      const response = await axiosInstance.get('/votes');
      setVotes(response.data.votes || []);
    } catch (err) {
      setError('Error fetching votes. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateVote = async (voteData) => {
    try {
      const response = await axiosInstance.post('/votes', voteData);
      setVotes((prevVotes) => [response.data.vote, ...prevVotes]); // Add new vote to the list
      setCreatedVoteLink(response.data.uniqueUrl); // Store the generated vote link
    } catch (err) {
      console.error('Error creating vote:', err);
      alert('Failed to create vote. Please try again.');
    }
  };

  const handleDeleteVote = async (voteId) => {
    const confirmDelete = window.confirm('Are you sure you want to delete this vote?');
    if (!confirmDelete) return;

    try {
      await axiosInstance.delete(`/votes/${voteId}`);
      setVotes((prevVotes) => prevVotes.filter((vote) => vote._id !== voteId));
      alert('Vote deleted successfully.');
    } catch (err) {
      console.error('Error deleting vote:', err);
      alert('Failed to delete vote. Please try again.');
    }
  };

  const handleCopyLink = (link) => {
    navigator.clipboard.writeText(link);
    alert('Vote link copied to clipboard!');
  };

  useEffect(() => {
    fetchVotes();
  }, []);

  return (
    <div className="am-voting-tab">
      <h2>Voting</h2>
      <AppButton className="am-voting-primary-button" onClick={handleOpenModal}>Hold Vote</AppButton>
      <CreateVoteModal
        isOpen={isModalOpen}
        onRequestClose={handleCloseModal}
        onCreateVote={handleCreateVote}
        createdVoteLink={createdVoteLink}
      />

      {isLoading ? (
        <p>Loading votes...</p>
      ) : error ? (
        <p className="am-voting-error-message">{error}</p>
      ) : votes.length > 0 ? (
        <ul className="am-votes-list">
          {votes.map((vote) => (
            <li key={vote._id} className="am-vote-item">
              <div>
                <strong>{vote.name} Vote</strong>
              </div>
              <div className="am-vote-buttons">
                <AppButton
                  kind="secondary"
                  className="am-copy-link-button"
                  onClick={() => handleCopyLink(vote.uniqueUrl)}
                >
                  Copy Link
                </AppButton>
                <AppButton kind="secondary" onClick={() => handleOpenResults(vote._id)}>View Results</AppButton>
                <AppButton kind="secondary" onClick={() => handleDeleteVote(vote._id)}>Delete</AppButton>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p>No votes found. Create one to get started!</p>
      )}

      {isResultsModalOpen && (
        <ResultsModal
          isOpen={isResultsModalOpen}
          onRequestClose={handleCloseResultsModal}
          vote={selectedVote}
        />
      )}
    </div>
  );
};

export default VotingTab;
